import React from 'react';

import { ActivityComposer } from 'src/components';

import type { ActivityLane } from './use-activity-lane';
import { ACTIVITY_LANE } from './use-activity-lane';
import type { TaskPanelState, TaskWithComments } from './use-task-panel-state';

interface TaskActivityComposerProps {
  isPreview: boolean;
  state: TaskPanelState;
  task: TaskWithComments;
}

/** The comment box under a task's activity, posting into the lane on screen. */
export function TaskActivityComposer({
  isPreview,
  state,
  task,
}: TaskActivityComposerProps): JSX.Element {
  const { activityController, activityLane, composerLane, composerRef, isExternalView } = state;
  const editorId = `task-composer:${task.id}:${activityLane.lane}`;

  return (
    <ActivityComposer
      /**
       * Remount per task and lane so an unsent draft doesn't leak into the next task's composer.
       * The composer can live outside the content keyed on the task, so nothing else remounts it.
       */
      key={editorId}
      ref={composerRef}
      floatingToolbar
      disabled={isPreview}
      editorId={editorId}
      lane={composerLane}
      peopleOnly={isExternalView}
      placeholder={getComposerPlaceholder(activityLane.showSwitch ? activityLane.lane : null)}
      workflowId={activityController.workflowId}
      onSubmit={state.handleSubmitComment}
      onSubmitAI={isExternalView ? undefined : state.handleSubmitAI}
    />
  );
}

function getComposerPlaceholder(lane: ActivityLane | null): string {
  if (lane === ACTIVITY_LANE.Team) return 'Comment to your team, type @ to mention someone…';
  if (lane === ACTIVITY_LANE.External) return 'Comment external, type @ to mention someone…';
  return 'Add a comment, type @ to mention someone…';
}
